import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  Card,
  fadeUp,
  stagger,
} from "@comai-portal/design-system";
import {
  blogHero,
  BlogPost,
  BlogCatalog,
  BlogSort,
  BLOG_SORTS,
  DEFAULT_BLOG_SORT,
  isBlogSort,
  BLOG_UNAVAILABLE_MESSAGE,
  mapApiPost,
  mapApiFolder,
  formatDate,
} from "./blogTypes";
import { blogAPI } from "../../services/api";
import { getCachedPosts, waitForCache } from "../../services/blogCache";

// CO 536 — posts come from the gateway's public `/api/blog`, backed by the
// marketing platform's blog lane on the BLOG_TENANT_ID tenant. No bundled
// fallback: an unreachable API renders the one honest line below instead of a
// stale list pretending to be the blog. Bodies render via <Prose> — no MUI.
//
// CO 540 — the blog is a CATALOG. With no control set, the page renders
// `/api/blog/series`: series folders (parts in reading order) then the
// standalone posts — the same projection `marketing_post_catalog` serves an
// agent. Choosing a sort or a category switches to the flat list served by
// `/api/blog?sort=&category=`, the same vocabulary `marketing_post_list`
// takes. Controls live in the URL so a view is linkable.

const SELECT_CLASS =
  "rounded-xl border border-ink/15 dark:border-paper/20 bg-paper dark:bg-ink px-3 py-2 text-sm text-ink dark:text-paper";

function PostCard({ post, eyebrow }: { post: BlogPost; eyebrow?: string }) {
  return (
    <Link to={`/blog/${post.slug}`} className="block h-full">
      <Card hover className="h-full flex flex-col">
        <div className="flex items-center gap-3 mb-3">
          <span className="ds-eyebrow">{eyebrow ?? post.category}</span>
          {post.readingTime != null && (
            <span className="text-sm text-mute dark:text-mute-soft">
              {post.readingTime} min read
            </span>
          )}
        </div>
        <h2 className="text-display-sm mb-2">{post.title}</h2>
        <p className="text-mute dark:text-mute-soft text-sm flex-grow">
          {post.excerpt}
        </p>
        <p className="text-sm text-mute dark:text-mute-soft mt-4">
          {post.author.name} · {formatDate(post.publishedAt)}
        </p>
      </Card>
    </Link>
  );
}

function catalogFromCache(): BlogCatalog | null {
  // The warm cache holds the flat list only; it seeds the first paint of the
  // standalone grid while the catalog read is in flight. Folders arrive with
  // the catalog — a cached row with a series is left for its folder.
  const cached = getCachedPosts();
  if (!cached) return null;
  return { series: [], standalone: cached.filter((p) => !p.series).map(mapApiPost) };
}

export function BlogListPage() {
  const [params, setParams] = useSearchParams();
  const sortParam = params.get("sort");
  const sort: BlogSort = isBlogSort(sortParam) ? sortParam : DEFAULT_BLOG_SORT;
  const category = params.get("category") ?? "";
  const filtered = sort !== DEFAULT_BLOG_SORT || category !== "";

  const [catalog, setCatalog] = useState<BlogCatalog | null>(catalogFromCache);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    blogAPI
      .getCategories()
      .then((cs) => {
        if (!cancelled) setCategories(cs.map((c) => c.name));
      })
      .catch(() => {
        /* the category control simply stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        if (filtered) {
          const { posts: apiPosts } = await blogAPI.getPosts({
            sort,
            ...(category ? { category } : {}),
            limit: 100,
          });
          if (cancelled) return;
          setPosts((apiPosts ?? []).map(mapApiPost));
        } else {
          await waitForCache();
          const c = await blogAPI.getCatalog();
          if (cancelled) return;
          setCatalog({ series: c.series.map(mapApiFolder), standalone: c.standalone.map(mapApiPost) });
        }
        setUnavailable(false);
      } catch {
        if (!cancelled) setUnavailable(true);
      }
    }
    load();

    return () => {
      cancelled = true;
    };
  }, [filtered, sort, category]);

  function setControl(name: "sort" | "category", value: string) {
    const next = new URLSearchParams(params);
    const isDefault = name === "sort" ? value === DEFAULT_BLOG_SORT : value === "";
    if (isDefault) next.delete(name);
    else next.set(name, value);
    setParams(next, { replace: true });
  }

  const nothingToShow = filtered
    ? posts.length === 0
    : !catalog || (catalog.series.length === 0 && catalog.standalone.length === 0);

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {blogHero.tagline}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              {blogHero.headline}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              {blogHero.subheadline}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      <Section tint="sunk">
        <Container>
          <form
            className="flex flex-wrap items-center gap-3 mb-8"
            aria-label="Browse the blog"
            onSubmit={(e) => e.preventDefault()}
          >
            <label className="flex items-center gap-2 text-sm text-mute dark:text-mute-soft">
              Sort
              <select
                className={SELECT_CLASS}
                value={sort}
                onChange={(e) => setControl("sort", e.target.value)}
              >
                {BLOG_SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-mute dark:text-mute-soft">
              Category
              <select
                className={SELECT_CLASS}
                value={category}
                onChange={(e) => setControl("category", e.target.value)}
              >
                <option value="">All</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {filtered && (
              <Link
                to="/blog"
                className="text-sm underline text-mute dark:text-mute-soft"
              >
                Show the catalog
              </Link>
            )}
          </form>

          {unavailable && nothingToShow ? (
            <p
              role="status"
              className="text-center text-lead text-mute dark:text-mute-soft"
            >
              {BLOG_UNAVAILABLE_MESSAGE}
            </p>
          ) : filtered ? (
            /*
              CO 174-002 — drive the grid with `animate`, NOT `whileInView`:
              cards mount AFTER the viewport check resolves (async load), and
              `whileInView` leaves late-mounting cards stuck at opacity 0.
            */
            <motion.div
              initial="hidden"
              animate="show"
              variants={stagger}
              className="grid gap-5 md:grid-cols-2"
            >
              {posts.map((post) => (
                <motion.div key={post.slug} variants={fadeUp}>
                  <PostCard post={post} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div initial="hidden" animate="show" variants={stagger}>
              {catalog?.series.map((folder) => (
                <motion.section
                  key={folder.key}
                  variants={fadeUp}
                  aria-label={`Series: ${folder.title}`}
                  className="mb-10"
                >
                  <Card className="flex flex-col">
                    <p className="ds-eyebrow mb-2">
                      Series · {folder.count} {folder.count === 1 ? "part" : "parts"}
                    </p>
                    <h2 className="text-display-sm mb-4">
                      <Link to={`/blog/series/${folder.key}`} className="hover:underline">
                        {folder.title}
                      </Link>
                    </h2>
                    <ol className="list-none p-0 m-0 grid gap-2 md:grid-cols-2">
                      {folder.posts.map((post) => (
                        <li key={post.slug}>
                          <Link
                            to={`/blog/${post.slug}`}
                            className="flex items-baseline gap-3 rounded-xl px-3 py-2 hover:bg-ink/5 dark:hover:bg-paper/10"
                          >
                            <span className="ds-eyebrow shrink-0">
                              Part {post.series?.part ?? "·"}
                            </span>
                            <span className="text-sm">{post.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ol>
                  </Card>
                </motion.section>
              ))}

              <motion.div
                variants={stagger}
                className="grid gap-5 md:grid-cols-2"
              >
                {catalog?.standalone.map((post) => (
                  <motion.div key={post.slug} variants={fadeUp}>
                    <PostCard post={post} />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </Container>
      </Section>
    </div>
  );
}
